import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';

const emptyRow         = () => ({ label: '', value: '', badge: '#dcfce7', color: '#16a34a' });
const emptyTable       = () => ({ title: '', icon: 'ri-list-check-2', rows: [] });
const emptyGeneralRow  = () => ({ t: '', d: '' });

const BADGE_PRESETS = [
  { label: 'Green (Refundable)',   badge: '#dcfce7', color: '#16a34a' },
  { label: 'Yellow (Partial)',     badge: '#fef3c7', color: '#d97706' },
  { label: 'Red (Non-refundable)', badge: '#fee2e2', color: '#dc2626' },
];

const ICON_OPTIONS = [
  { label: 'Checklist',    value: 'ri-list-check-2' },
  { label: 'Flight',       value: 'ri-flight-takeoff-line' },
  { label: 'Hotel',        value: 'ri-hotel-line' },
  { label: 'Bus',          value: 'ri-bus-line' },
  { label: 'Train',        value: 'ri-train-line' },
  { label: 'Car',          value: 'ri-car-line' },
  { label: 'Ship / Cruise',value: 'ri-ship-line' },
  { label: 'Package Tour', value: 'ri-suitcase-line' },
  { label: 'Visa',         value: 'ri-passport-line' },
  { label: 'Insurance',    value: 'ri-shield-check-line' },
  { label: 'Activity',     value: 'ri-map-pin-line' },
  { label: 'Refund',       value: 'ri-refund-2-line' },
];

// ── HELPER COMPONENTS (defined outside to avoid focus-stealing remounts) ────

const SectionHeader = ({ label }) => (
  <h6 className="text-primary fw-bold text-uppercase small mb-4" style={{ letterSpacing: '1px' }}>
    {label}
  </h6>
);

const FieldRow = ({ label, children }) => (
  <div className="mb-3">
    <Form.Label className="small fw-bold">{label}</Form.Label>
    {children}
  </div>
);

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────

const RefundPolicyEditor = ({ workingData, updateSection, onSave, isSaving }) => {

  const [rp, setRp] = useState(() => ({
    heroTitle:      'Refund & Cancellation Policy',
    heroSubtitle:   'Transparent policies for a worry-free booking experience',
    heroBgImage:    'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1800&q=50',
    infoNote:       "Refund policies depend on the service booked and the respective supplier's terms. The timelines below are indicative. Please check the specific cancellation policy displayed during checkout for your booking.",
    cancelTables:   [],   // ← dynamic: [{ title, icon, rows: [{label,value,badge,color}] }]
    generalRows:    [],
    ctaText:        'Need help with a cancellation or refund?',
    ctaButtonLabel: 'Contact Support',
    ctaButtonLink:  '/contact-us',
    // DB wins
    ...workingData?.refundPolicy,
  }));

  const set = (key, val) => setRp(prev => ({ ...prev, [key]: val }));

  // ── Cancellation TABLES ──────────────────────────────────────────────────
  const addTable    = ()        => setRp(prev => ({ ...prev, cancelTables: [...prev.cancelTables, emptyTable()] }));
  const removeTable = (ti)      => setRp(prev => ({ ...prev, cancelTables: prev.cancelTables.filter((_, i) => i !== ti) }));
  const moveTable   = (ti, dir) => setRp(prev => {
    const arr  = [...prev.cancelTables];
    const swap = ti + dir;
    if (swap < 0 || swap >= arr.length) return prev;
    [arr[ti], arr[swap]] = [arr[swap], arr[ti]];
    return { ...prev, cancelTables: arr };
  });
  const updateTableField = (ti, field, val) => setRp(prev => {
    const arr  = [...prev.cancelTables];
    arr[ti]    = { ...arr[ti], [field]: val };
    return { ...prev, cancelTables: arr };
  });

  // ── Rows INSIDE a table ──────────────────────────────────────────────────
  const addRow    = (ti)           => setRp(prev => {
    const arr  = [...prev.cancelTables];
    arr[ti]    = { ...arr[ti], rows: [...arr[ti].rows, emptyRow()] };
    return { ...prev, cancelTables: arr };
  });
  const removeRow = (ti, ri)       => setRp(prev => {
    const arr  = [...prev.cancelTables];
    arr[ti]    = { ...arr[ti], rows: arr[ti].rows.filter((_, i) => i !== ri) };
    return { ...prev, cancelTables: arr };
  });
  const moveRow   = (ti, ri, dir)  => setRp(prev => {
    const arr  = [...prev.cancelTables];
    const rows = [...arr[ti].rows];
    const swap = ri + dir;
    if (swap < 0 || swap >= rows.length) return prev;
    [rows[ri], rows[swap]] = [rows[swap], rows[ri]];
    arr[ti] = { ...arr[ti], rows };
    return { ...prev, cancelTables: arr };
  });
  const updateRow = (ti, ri, field, val) => setRp(prev => {
    const arr  = [...prev.cancelTables];
    const rows = [...arr[ti].rows];
    rows[ri]   = { ...rows[ri], [field]: val };
    arr[ti]    = { ...arr[ti], rows };
    return { ...prev, cancelTables: arr };
  });

  // ── General policy rows ──────────────────────────────────────────────────
  const addGeneralRow    = ()             => setRp(prev => ({ ...prev, generalRows: [...prev.generalRows, emptyGeneralRow()] }));
  const removeGeneralRow = (idx)          => setRp(prev => ({ ...prev, generalRows: prev.generalRows.filter((_, i) => i !== idx) }));
  const updateGeneralRow = (idx, f, val)  => setRp(prev => {
    const arr = [...prev.generalRows]; arr[idx] = { ...arr[idx], [f]: val };
    return { ...prev, generalRows: arr };
  });
  const moveGeneralRow   = (idx, dir)     => setRp(prev => {
    const arr  = [...prev.generalRows];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return prev;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    return { ...prev, generalRows: arr };
  });

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    updateSection('refundPolicy', rp);
    if (onSave) await onSave(rp);
    toast.success('Refund Policy saved successfully!');
  };

  const totalRows = rp.cancelTables.reduce((s, t) => s + t.rows.length, 0);

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={11}>
          <Card className="shadow-sm border-0 rounded-4">

            <Card.Header className="bg-white py-3 border-bottom">
              <h5 className="fw-bold mb-0">
                <i className="ri-refund-2-line me-2 text-primary" />
                Refund &amp; Cancellation Policy — Content Editor
              </h5>
            </Card.Header>

            <Card.Body className="p-0">
              <Accordion flush>

                {/* ── 1. HERO ─────────────────────────────────── */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <i className="ri-image-2-line me-2 text-primary" />
                    <strong>Hero Section</strong>
                    <span className="ms-2 text-muted small">— Page banner title &amp; subtitle</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="1. Hero Banner" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="Page Title">
                          <Form.Control value={rp.heroTitle} placeholder="Refund & Cancellation Policy"
                            onChange={e => set('heroTitle', e.target.value)} />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Subtitle">
                          <Form.Control value={rp.heroSubtitle} placeholder="Transparent policies for a worry-free booking experience"
                            onChange={e => set('heroSubtitle', e.target.value)} />
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Background Image URL">
                          <Form.Control value={rp.heroBgImage} placeholder="https://images.unsplash.com/..."
                            onChange={e => set('heroBgImage', e.target.value)} />
                          {rp.heroBgImage && (
                            <img src={rp.heroBgImage} alt="preview" className="mt-2 rounded"
                              style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', opacity: 0.85 }}
                              onError={e => e.target.style.display = 'none'} />
                          )}
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 2. INFO NOTE ─────────────────────────────── */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <i className="ri-information-line me-2 text-primary" />
                    <strong>Info Note</strong>
                    <span className="ms-2 text-muted small">— Disclaimer shown above the policy tables</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="2. Info / Disclaimer Note" />
                    <FieldRow label="Note Text">
                      <Form.Control as="textarea" rows={3} value={rp.infoNote}
                        placeholder="Refund policies depend on the service booked..."
                        onChange={e => set('infoNote', e.target.value)} />
                    </FieldRow>
                    <small className="text-muted"><i className="ri-information-line me-1" />Leave empty to hide this note.</small>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 3. CANCELLATION TABLES (dynamic) ────────── */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <i className="ri-table-line me-2 text-primary" />
                    <strong>Cancellation Tables</strong>
                    <span className="ms-2 text-muted small">
                      — {rp.cancelTables.length} table{rp.cancelTables.length !== 1 ? 's' : ''}, {totalRows} row{totalRows !== 1 ? 's' : ''}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="3. Cancellation Policy Tables" />

                    {rp.cancelTables.length === 0 && (
                      <div className="text-center py-4 text-muted border rounded bg-light mb-3">
                        <i className="ri-table-line" style={{ fontSize: '32px', display: 'block', marginBottom: '8px', opacity: 0.4 }} />
                        <p className="small mb-1 fw-bold">No tables yet.</p>
                        <p className="small mb-0">Click "Add Cancellation Table" to add Flight, Hotel, Bus, or any custom category.</p>
                      </div>
                    )}

                    {rp.cancelTables.map((tbl, ti) => (
                      <div key={ti} className="border rounded-3 mb-4 overflow-hidden shadow-sm">

                        {/* Table header bar */}
                        <div className="d-flex align-items-center justify-content-between px-3 py-2"
                          style={{ background: '#eef2ff', borderBottom: '1px solid #dee2e6' }}>
                          <div className="d-flex align-items-center gap-2">
                            <i className={`${tbl.icon || 'ri-list-check-2'} text-primary`} style={{ fontSize: '16px' }} />
                            <span className="fw-bold small text-primary">
                              Table {ti + 1}{tbl.title ? ` — ${tbl.title}` : ''}
                            </span>
                            <span className="badge bg-primary ms-1" style={{ fontSize: '10px' }}>
                              {tbl.rows.length} row{tbl.rows.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="d-flex gap-1">
                            <Button variant="outline-secondary" size="sm" disabled={ti === 0}
                              style={{ padding: '2px 7px' }} title="Move table up"
                              onClick={() => moveTable(ti, -1)}>
                              <i className="ri-arrow-up-line" />
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={ti === rp.cancelTables.length - 1}
                              style={{ padding: '2px 7px' }} title="Move table down"
                              onClick={() => moveTable(ti, 1)}>
                              <i className="ri-arrow-down-line" />
                            </Button>
                            <Button variant="outline-danger" size="sm" style={{ padding: '2px 7px' }}
                              title="Delete this table"
                              onClick={() => removeTable(ti)}>
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </div>
                        </div>

                        <div className="p-3">
                          {/* Table name + icon */}
                          <Row className="g-2 mb-3">
                            <Col md={7}>
                              <Form.Label className="small fw-bold">Table Title</Form.Label>
                              <Form.Control value={tbl.title} placeholder="e.g. Flight Cancellations"
                                onChange={e => updateTableField(ti, 'title', e.target.value)} />
                            </Col>
                            <Col md={5}>
                              <Form.Label className="small fw-bold">Icon</Form.Label>
                              <Form.Select value={tbl.icon}
                                onChange={e => updateTableField(ti, 'icon', e.target.value)}>
                                {ICON_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </Form.Select>
                            </Col>
                          </Row>

                          {/* Rows inside this table */}
                          {tbl.rows.length === 0 && (
                            <div className="text-center py-3 text-muted border rounded bg-light mb-2">
                              <p className="small mb-0">No rows yet. Click "Add Row" below.</p>
                            </div>
                          )}

                          {tbl.rows.map((r, ri) => (
                            <div key={ri} className="border rounded p-3 pt-4 mb-2 bg-white position-relative">
                              <div className="d-flex gap-1" style={{ position: 'absolute', top: '8px', right: '10px', zIndex: 10 }}>
                                <Button variant="outline-secondary" size="sm" disabled={ri === 0}
                                  style={{ padding: '2px 6px' }} onClick={e => { e.stopPropagation(); moveRow(ti, ri, -1); }}>
                                  <i className="ri-arrow-up-line" />
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={ri === tbl.rows.length - 1}
                                  style={{ padding: '2px 6px' }} onClick={e => { e.stopPropagation(); moveRow(ti, ri, 1); }}>
                                  <i className="ri-arrow-down-line" />
                                </Button>
                                <Button variant="outline-danger" size="sm" style={{ padding: '2px 6px' }}
                                  onClick={e => { e.stopPropagation(); removeRow(ti, ri); }}>
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </div>

                              <span className="badge bg-secondary mb-2" style={{ fontSize: '10px' }}>Row {ri + 1}</span>

                              <Row className="g-2">
                                <Col md={6}>
                                  <Form.Label className="small fw-bold">Condition Label</Form.Label>
                                  <Form.Control value={r.label} placeholder="> 24 hours before departure"
                                    onChange={e => updateRow(ti, ri, 'label', e.target.value)} />
                                </Col>
                                <Col md={6}>
                                  <Form.Label className="small fw-bold">Refund Value</Form.Label>
                                  <Form.Control value={r.value} placeholder="Full refund"
                                    onChange={e => updateRow(ti, ri, 'value', e.target.value)} />
                                </Col>
                                <Col md={12}>
                                  <Form.Label className="small fw-bold">Badge Colour</Form.Label>
                                  <Form.Select value={`${r.badge}|${r.color}`}
                                    onChange={e => {
                                      const [badge, color] = e.target.value.split('|');
                                      updateRow(ti, ri, 'badge', badge);
                                      updateRow(ti, ri, 'color', color);
                                    }}>
                                    {BADGE_PRESETS.map(p => (
                                      <option key={p.label} value={`${p.badge}|${p.color}`}>{p.label}</option>
                                    ))}
                                  </Form.Select>
                                </Col>
                              </Row>

                              {r.value && (
                                <div className="mt-2">
                                  <small className="text-muted fw-bold">Preview: </small>
                                  <span style={{ background: r.badge, color: r.color, fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px' }}>
                                    {r.value}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}

                          <Button variant="outline-primary" size="sm" className="mt-1 d-flex align-items-center gap-1 fw-bold"
                            onClick={() => addRow(ti)}>
                            <i className="ri-add-line" /> Add Row
                          </Button>
                        </div>
                      </div>
                    ))}

                    <Button variant="primary" size="sm" className="mt-1 fw-bold d-flex align-items-center gap-2"
                      onClick={addTable}>
                      <i className="ri-add-circle-fill" /> Add Cancellation Table
                    </Button>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 4. GENERAL POLICY ───────────────────────── */}
                <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    <i className="ri-file-list-3-line me-2 text-primary" />
                    <strong>General Refund Process</strong>
                    <span className="ms-2 text-muted small">
                      — {rp.generalRows.length} item{rp.generalRows.length !== 1 ? 's' : ''}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="4. General Refund Process Items" />

                    {rp.generalRows.length === 0 && (
                      <div className="text-center py-4 text-muted border rounded bg-light mb-3">
                        <i className="ri-file-list-3-line" style={{ fontSize: '32px', display: 'block', marginBottom: '8px', opacity: 0.4 }} />
                        <p className="small mb-0">No items yet. Click "Add Item" to get started.</p>
                      </div>
                    )}

                    {rp.generalRows.map((r, i) => (
                      <div key={i} className="border rounded p-3 pt-4 mb-3 bg-light position-relative">
                        <div className="d-flex gap-1" style={{ position: 'absolute', top: '8px', right: '10px', zIndex: 10 }}>
                          <Button variant="outline-secondary" size="sm" disabled={i === 0} style={{ padding: '2px 7px' }}
                            onClick={e => { e.stopPropagation(); moveGeneralRow(i, -1); }}>
                            <i className="ri-arrow-up-line" />
                          </Button>
                          <Button variant="outline-secondary" size="sm" disabled={i === rp.generalRows.length - 1} style={{ padding: '2px 7px' }}
                            onClick={e => { e.stopPropagation(); moveGeneralRow(i, 1); }}>
                            <i className="ri-arrow-down-line" />
                          </Button>
                          <Button variant="outline-danger" size="sm" style={{ padding: '2px 7px' }}
                            onClick={e => { e.stopPropagation(); removeGeneralRow(i); }}>
                            <i className="ri-delete-bin-line" />
                          </Button>
                        </div>
                        <span className="badge bg-primary mb-2" style={{ fontSize: '11px' }}>Item {i + 1}</span>
                        <Form.Label className="small fw-bold d-block">Title</Form.Label>
                        <Form.Control value={r.t} placeholder="Refund Timeline" className="mb-2"
                          onChange={e => updateGeneralRow(i, 't', e.target.value)} />
                        <Form.Label className="small fw-bold d-block">Description</Form.Label>
                        <Form.Control as="textarea" rows={3} value={r.d}
                          placeholder="Refunds are typically processed within 7–21 business days..."
                          onChange={e => updateGeneralRow(i, 'd', e.target.value)} />
                      </div>
                    ))}

                    <Button variant="outline-primary" size="sm" className="mt-2 fw-bold d-flex align-items-center gap-2" onClick={addGeneralRow}>
                      <i className="ri-add-circle-line" /> Add Item
                    </Button>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 5. CTA ──────────────────────────────────── */}
                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    <i className="ri-customer-service-2-line me-2 text-primary" />
                    <strong>Call-to-Action</strong>
                    <span className="ms-2 text-muted small">— "Contact Support" block at the bottom</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="5. CTA Block" />
                    <Row className="g-3">
                      <Col md={12}>
                        <FieldRow label="CTA Text">
                          <Form.Control value={rp.ctaText} placeholder="Need help with a cancellation or refund?"
                            onChange={e => set('ctaText', e.target.value)} />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Button Label">
                          <Form.Control value={rp.ctaButtonLabel} placeholder="Contact Support"
                            onChange={e => set('ctaButtonLabel', e.target.value)} />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Button Link">
                          <Form.Control value={rp.ctaButtonLink} placeholder="/contact-us"
                            onChange={e => set('ctaButtonLink', e.target.value)} />
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

              </Accordion>
            </Card.Body>

            {/* ── SAVE FOOTER ── */}
            <Card.Footer className="bg-white border-top py-3">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="ri-information-line me-1 text-primary" />
                  {rp.cancelTables.length} table{rp.cancelTables.length !== 1 ? 's' : ''} · {totalRows} row{totalRows !== 1 ? 's' : ''} · {rp.generalRows.length} general item{rp.generalRows.length !== 1 ? 's' : ''} ready to publish.
                </small>
                <Button variant="primary" className="px-5 fw-bold shadow" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'SAVE REFUND POLICY ✦'}
                </Button>
              </div>
            </Card.Footer>

          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RefundPolicyEditor;